import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { applicationUrls } from '../../../environments/environment';
import { ALL_IAG_ROLES, CUSTODIAN_ROLES, FRM_NONPROD_ADMIN } from '../constants/iam-roles.constants';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly EXTERNAL_INTG_REDIRECT_PATH = '/mfrmui?cdxRedirectPath=/';
  private static readonly LOCAL_DEV_USER_LABEL_STORAGE_KEY = 'frm.dev.userLabel';
  private static readonly AUTH_STORAGE_KEYS = [
    'x-fnma-jws-token',
    'access_token',
    'accessToken',
    'id_token',
    'idToken',
    'jwt',
    'token',
    'sessionId',
    'x-fnma-sessionid',
    'frm.dev.userLabel',
    'frm.dev.roles',
    'frm.dev.isExternalUser',
    'frm.dev.mornetID',
    'frm.dev.dealerOrgName'
  ] as const;

  isAuthenticated = signal(false);
  userRoles = signal<string[]>([]);
  userInfo = signal<UserInfoResponse | null>(null);
  accessToken = signal<string>('');
  private logoutInProgress = false;

  constructor(private readonly http: HttpClient) {}

  logout(): void {
    if (this.logoutInProgress) {
      console.info('[AuthService] logout already in progress; ignoring duplicate request.');
      return;
    }

    this.logoutInProgress = true;
    console.info('[AuthService] logout started.');

    const isInternal = this.resolveIsInternalUser();
    console.info('[AuthService] user type resolved for logout.', { isInternal });

    const redirectUrl = this.getLogoutRedirectUrl(isInternal);
    console.info('[AuthService] logout redirect URL selected.', { redirectUrl });

    this.clearClientState();
    console.info('[AuthService] client state cleared.');

    console.info('[AuthService] redirect initiated.');
    const location = this.getCurrentLocation();
    location.assign(redirectUrl);
  }

  resolveIsInternalUser(): boolean | null {
    const details = this.userInfo()?.userDetails;
    if (!details) {
      return null;
    }

    if (typeof details.isInternalUser === 'boolean') {
      return details.isInternalUser;
    }

    if (typeof details.isExternalUser === 'boolean') {
      return !details.isExternalUser;
    }

    if (typeof details.isExternalUser === 'string') {
      const normalized = details.isExternalUser.trim().toLowerCase();
      if (normalized === 'true') {
        return false;
      }
      if (normalized === 'false') {
        return true;
      }
    }

    if (typeof details.mornetID === 'string' && details.mornetID.trim().length > 0) {
      return false;
    }

    const normalizedRoles = new Set(this.userRoles().map((role) => this.normalizeRole(role)));
    const hasExternalRole = [...normalizedRoles].some((role) => role.includes('LENDER') || role.includes('CUSTODIAN'));
    if (hasExternalRole) {
      return false;
    }

    const hasInternalRole = [...normalizedRoles].some((role) =>
      role.includes('ADMIN') || role.includes('ANALYST') || role.includes('READONLY')
    );
    if (hasInternalRole) {
      return true;
    }

    return null;
  }

  getLogoutRedirectUrl(isInternal: boolean | null): string {
    const location = this.getCurrentLocation();
    const appConfig = applicationUrls[location.hostname] ?? applicationUrls.localhost;
    const internalUrl = this.normalizeRedirectUrl(appConfig?.LogoutUrlInternal ?? appConfig?.internalLogoutUrl);
    const externalUrl = this.normalizeRedirectUrl(appConfig?.LogoutUrlExternal ?? appConfig?.externalLogoutUrl);

    const primary = isInternal === true ? internalUrl : externalUrl;
    if (primary) {
      return primary;
    }

    if (externalUrl) {
      return externalUrl;
    }

    return `${location.origin}/`;
  }

  clearClientState(): void {
    this.clearStorageKeys(window.localStorage, AuthService.AUTH_STORAGE_KEYS);
    this.clearStorageKeys(window.sessionStorage, AuthService.AUTH_STORAGE_KEYS);

    this.clearAuthentication();
    this.setSelectedLocalDevUserLabel(null);
    this.clearAppOwnedCookies();
  }

  private normalizeRedirectUrl(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    try {
      return new URL(trimmed, window.location.origin).toString();
    } catch {
      return null;
    }
  }

  private clearStorageKeys(storage: Storage, keys: readonly string[]): void {
    keys.forEach((key) => storage.removeItem(key));
  }

  private clearAppOwnedCookies(): void {
    if (typeof document === 'undefined' || !document.cookie) {
      return;
    }

    const cookieNames = document.cookie
      .split(';')
      .map((pair) => pair.split('=')[0]?.trim())
      .filter((name): name is string => !!name)
      .filter((name) => {
        const lower = name.toLowerCase();
        return lower.startsWith('frm')
          || lower.startsWith('x-fnma-')
          || lower.includes('token')
          || lower.includes('session');
      });

    Array.from(new Set(cookieNames)).forEach((name) => {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  }

  async initializeUserDetailsAndRedirectToLogin(): Promise<boolean> {
    const location = this.getCurrentLocation();
    const hostname = location.hostname;
    const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(hostname);
    const appConfig = applicationUrls[hostname] ?? applicationUrls.localhost;
    const userDetailsPath = appConfig?.userDetailsPath ?? '/userdetails';
    const loginRedirectPath = appConfig?.loginRedirectPath ?? '/mfrm/api?cdxRedirectPath=/';
    const userDetailsUrl = `${location.origin}${userDetailsPath}`;
    const redirectUrl = this.resolveLoginRedirectUrl(location.origin, hostname, loginRedirectPath);

    if (isLocalHost) {
      const fallbackRoles = appConfig?.localDevRoles ?? [FRM_NONPROD_ADMIN];
      const localDevUserLabel = this.getSelectedLocalDevUserLabel() ?? appConfig?.localDevUserLabel;
      const localDevIdentity = await this.resolveLocalDevIdentity(fallbackRoles, localDevUserLabel);
      this.setAuthenticatedUser({
        userDetails: localDevIdentity
      });
      return true;
    }

    try {
      const response = await firstValueFrom(
        this.http.get<UserInfoResponse>(userDetailsUrl, { withCredentials: true })
      );
      const normalizedResponse = this.normalizeRemoteUserResponse(response);
      if (!normalizedResponse) {
        throw new Error('Received empty userdetails response');
      }
      this.setAuthenticatedUser(normalizedResponse);
      return true;
    } catch {
      this.clearAuthentication();
      location.replace(redirectUrl);
      return false;
    }
  }

  getUserInfo(): UserInfoResponse | null {
    return this.userInfo();
  }

  getUserId(): string {
    return this.userInfo()?.userDetails?.uid ?? '';
  }

  /** Returns the authenticated user's userDetails.userName (falls back to uid if absent) */
  getUserName(): string {
    const userName = this.userInfo()?.userDetails?.['userName'];
    return typeof userName === 'string' && userName.trim() !== '' ? userName : this.getUserId();

  }   
  /** Returns a human-readable display name (e.g. "Jamie Analyst") for the current user, if known */
  getUserDisplayName(): string {
    const userDetails = this.userInfo()?.userDetails;
    const fullName = [userDetails?.firstName, userDetails?.lastName]
      .filter((part): part is string => !!part && part.trim().length > 0)
      .join(' ')
      .trim();
    return fullName || this.getUserId();
  }

  getAccessToken(): string {
    return this.accessToken();
  }

  /** Checks whether the authenticated user has at least one of the given IAG role names */
  hasRole(...roles: string[]): boolean {
    const availableRoles = new Set(this.userRoles().map((r) => this.normalizeRole(r)));
    return roles.some((role) => availableRoles.has(this.normalizeRole(role)));
  }

  /** Returns the authenticated user's lender context (mornetID) if external; undefined for internal users */
  getLenderContext(): string | undefined {
    return this.userInfo()?.userDetails?.mornetID ?? undefined;
  }

  /** Returns the authenticated user's seller-servicer id when present. */
  getSellerServicerCoNo(): string | undefined {
    const value = this.userInfo()?.userDetails?.sellerServicerCoNo;
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
  }

  /** Returns true if the authenticated user is a lender (external); false for internal Fannie Mae users */
  isExternalUser(): boolean {
    return this.resolveExternalUserValue(this.userInfo()?.userDetails ?? {});
  }

  /** Returns the authenticated user's lender display name (dealer organization name) if available */
  getLenderDisplayName(): string | undefined {
    return this.userInfo()?.userDetails?.dealerOrgName ?? undefined;
  }

  isLocalDevMode(): boolean {
    return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  }

  getSelectedLocalDevUserLabel(): string | null {
    if (!this.isLocalDevMode()) {
      return null;
    }

    const value = window.localStorage.getItem(AuthService.LOCAL_DEV_USER_LABEL_STORAGE_KEY);
    return typeof value === 'string' && value.trim() ? value.trim() : null;
  }

  setSelectedLocalDevUserLabel(userLabel: string | null): void {
    if (!this.isLocalDevMode()) {
      return;
    }

    const normalized = userLabel?.trim();
    if (normalized) {
      window.localStorage.setItem(AuthService.LOCAL_DEV_USER_LABEL_STORAGE_KEY, normalized);
      return;
    }

    window.localStorage.removeItem(AuthService.LOCAL_DEV_USER_LABEL_STORAGE_KEY);
  }

  async getLocalDevPersonaLabels(): Promise<string[]> {
    if (!this.isLocalDevMode()) {
      return [];
    }

    try {
      const catalog = await firstValueFrom(this.http.get<DevUserCatalogEntry[]>('/dev-users.json'));
      if (!Array.isArray(catalog)) {
        return [];
      }

      return catalog
        .map((entry) => entry.label?.trim())
        .filter((label): label is string => !!label);
    } catch {
      return [];
    }
  }

  async switchLocalDevPersona(userLabel: string): Promise<void> {
    this.setSelectedLocalDevUserLabel(userLabel);

    const targetPath = await this.resolveLocalDevPersonaTargetPath(userLabel);
    window.location.assign(`${window.location.origin}${targetPath}`);
  }

  private setAuthenticatedUser(response: UserInfoResponse): void {
    this.userInfo.set(response);
    const groups = response?.userDetails?.groups ?? [];
    this.userRoles.set(groups);
    this.accessToken.set(this.resolveAccessToken());
    this.isAuthenticated.set(true);
  }

  private clearAuthentication(): void {
    this.userInfo.set(null);
    this.userRoles.set([]);
    this.accessToken.set('');
    this.isAuthenticated.set(false);
  }

  private resolveAccessToken(): string {
    // /userdetails is identity-focused in our environments; token is resolved from browser storage.
    const storageKeys = [
      'x-fnma-jws-token',
      'access_token',
      'accessToken',
      'id_token',
      'idToken',
      'jwt',
      'token'
    ];

    for (const key of storageKeys) {
      const fromSession = window.sessionStorage.getItem(key);
      if (fromSession) {
        return fromSession;
      }

      const fromLocal = window.localStorage.getItem(key);
      if (fromLocal) {
        return fromLocal;
      }
    }

    return '';
  }

  private getCurrentLocation(): Pick<Location, 'hostname' | 'origin' | 'replace' | 'assign'> {
    return window.location;
  }

  private resolveLoginRedirectUrl(origin: string, hostname: string, defaultRedirectPath: string): string {
    if (hostname.toLowerCase().endsWith('.intgfanniemae.com')) {
      return `${origin}${AuthService.EXTERNAL_INTG_REDIRECT_PATH}`;
    }

    return `${origin}${defaultRedirectPath}`;
  }

  private normalizeToken(value: unknown): string {
    if (typeof value !== 'string') {
      return '';
    }

    const token = value.trim();
    if (!token) {
      return '';
    }

    return token.startsWith('Bearer ') ? token.slice(7).trim() : token;
  }

  private async resolveLocalDevIdentity(
    fallbackRoles: readonly string[],
    userLabel?: string
  ): Promise<UserDetails> {
    const fallbackIdentity = this.buildFallbackLocalDevIdentity(fallbackRoles);

    try {
      const catalog = await firstValueFrom(this.http.get<DevUserCatalogEntry[]>('/dev-users.json'));
      if (!Array.isArray(catalog) || catalog.length === 0) {
        return fallbackIdentity;
      }

      const selectedCatalogUser = this.selectCatalogUser(catalog, userLabel);
      if (!selectedCatalogUser?.userDetails) {
        return fallbackIdentity;
      }

      return this.normalizeCatalogUserDetails(selectedCatalogUser.userDetails, fallbackIdentity);
    } catch {
      return fallbackIdentity;
    }
  }

  private async resolveLocalDevPersonaTargetPath(userLabel: string): Promise<string> {
    try {
      const catalog = await firstValueFrom(this.http.get<DevUserCatalogEntry[]>('/dev-users.json'));
      if (!Array.isArray(catalog) || catalog.length === 0) {
        return '/';
      }

      const selectedCatalogUser = this.selectCatalogUser(catalog, userLabel);
      const userDetails = selectedCatalogUser?.userDetails;
      if (!userDetails || !this.resolveExternalUserValue(userDetails)) {
        return '/';
      }

      // Custodian wins over lender roles, matching externalLandingGuard.
      const custodianRoles = new Set<string>(CUSTODIAN_ROLES.map((role) => this.normalizeRole(role)));
      const isCustodian = (userDetails.groups ?? []).some((group) => custodianRoles.has(this.normalizeRole(group)));
      return isCustodian ? '/custodian/reserve-requirement-summary' : '/lender-overview/external-lenders';
    } catch {
      return '/';
    }
  }

  /**
   * Selects a dev-users.json entry by its `label` field (case-insensitive, trimmed match).
   * Falls back to the first catalog entry when `userLabel` is unset or matches no entry, so
   * omitting the config preserves the previous "always use the first user" behavior.
   */
  private selectCatalogUser(
    catalog: DevUserCatalogEntry[],
    userLabel?: string
  ): DevUserCatalogEntry | undefined {
    const normalizedLabel = userLabel?.trim().toLowerCase();
    if (!normalizedLabel) {
      return catalog[0];
    }

    const matchedUser = catalog.find((entry) => entry.label?.trim().toLowerCase() === normalizedLabel);
    return matchedUser ?? catalog[0];
  }

  private buildFallbackLocalDevIdentity(fallbackRoles: readonly string[]): UserDetails {
    return {
      uid: 'local-dev',
      firstName: 'Local',
      lastName: 'Developer',
      groups: this.pickFirstKnownRole(fallbackRoles),
      isExternalUser: false,
      mornetID: null,
      dealerOrgName: null
    };
  }

  private normalizeCatalogUserDetails(userDetails: UserDetails, fallbackIdentity: UserDetails): UserDetails {
    const roleCandidates = Array.isArray(userDetails.groups) ? userDetails.groups : [];

    return {
      ...userDetails,
      uid: this.resolveOptionalString(userDetails.uid) ?? fallbackIdentity.uid,
      firstName: this.resolveOptionalString(userDetails.firstName) ?? fallbackIdentity.firstName,
      lastName: this.resolveOptionalString(userDetails.lastName) ?? fallbackIdentity.lastName,
      groups: this.pickFirstKnownRole(roleCandidates),
      isExternalUser: this.resolveExternalUserValue(userDetails),
      mornetID: this.resolveOptionalString(userDetails.mornetID),
      dealerOrgName: this.resolveOptionalString(userDetails.dealerOrgName)
    };
  }

  private normalizeRemoteUserResponse(response: UserInfoResponse | null | undefined): UserInfoResponse | null {
    const userDetails = response?.userDetails;
    if (!userDetails) {
      return null;
    }

    const normalized: UserDetails = {
      ...userDetails,
      uid: this.resolveOptionalString(userDetails.uid) ?? undefined,
      userName: this.resolveOptionalString(userDetails.userName) ?? undefined,
      firstName: this.resolveOptionalString(userDetails.firstName) ?? undefined,
      lastName: this.resolveOptionalString(userDetails.lastName) ?? undefined,
      groups: this.filterToKnownRoles(Array.isArray(userDetails.groups) ? userDetails.groups : []),
      isExternalUser: this.resolveExternalUserValue(userDetails),
      mornetID: this.resolveOptionalString(userDetails.mornetID),
      dealerOrgName: this.resolveOptionalString(userDetails.dealerOrgName),
      sellerServicerCoNo: this.resolveOptionalString(userDetails.sellerServicerCoNo) ?? undefined,
    };

    if (!this.hasUsableIdentity(normalized)) {
      return null;
    }

    return { ...response, userDetails: normalized };
  }

  private hasUsableIdentity(userDetails: UserDetails): boolean {
    return Boolean(
      userDetails.uid
      || userDetails.userName
      || userDetails.firstName
      || userDetails.lastName
      || userDetails.mornetID
      || userDetails.dealerOrgName
      || (Array.isArray(userDetails.groups) && userDetails.groups.length > 0)
    );
  }

  private resolveExternalUserValue(userDetails: UserDetails): boolean {
    if (typeof userDetails.isExternalUser === 'boolean') {
      return userDetails.isExternalUser;
    }

    if (typeof userDetails.isExternalUser === 'string') {
      return userDetails.isExternalUser.trim().toLowerCase() === 'true';
    }

    if (typeof userDetails.isInternalUser === 'boolean') {
      return !userDetails.isInternalUser;
    }

    return false;
  }

  private resolveOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
  }

  private pickFirstKnownRole(inputRoles: readonly unknown[]): string[] {
    for (const role of inputRoles) {
      if (typeof role !== 'string') {
        continue;
      }

      const matchedRoles = this.filterToKnownRoles([role]);
      if (matchedRoles.length > 0) {
        return [matchedRoles[0]];
      }
    }

    return [];
  }

  private filterToKnownRoles(inputRoles: readonly unknown[]): string[] {
    const knownRolesByNormalizedValue = new Map<string, string>(
      ALL_IAG_ROLES.map((role) => [this.normalizeRole(role), role])
    );

    const filteredRoles: string[] = [];
    for (const role of inputRoles) {
      if (typeof role !== 'string') {
        continue;
      }

      const normalizedRole = this.normalizeRole(role);
      const canonicalRole = knownRolesByNormalizedValue.get(normalizedRole);
      if (!canonicalRole || filteredRoles.includes(canonicalRole)) {
        continue;
      }

      filteredRoles.push(canonicalRole);
    }

    return filteredRoles;
  }


  private normalizeRole(role: string): string {
    return role.trim().toUpperCase();
  }
}

interface UserDetails {
  uid?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  groups?: string[];
  isExternalUser?: boolean | string | null;
  isInternalUser?: boolean;
  mornetID?: string | null;
  dealerOrgName?: string | null;
  sellerServicerCoNo?: string;
  [key: string]: unknown;
}

interface UserInfoResponse {
  userDetails?: UserDetails;
}

interface DevUserCatalogEntry {
  label?: string;
  userDetails?: UserDetails;
}
